import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Search, ChevronRight, ThumbsUp, ThumbsDown, Mail, Home, Heart, CheckCircle, Loader2, Volume2, X, Send, MessageCircle } from 'lucide-react';
import { BugBountyCard } from '../components/BugBounty';
import { helpCategories, helpArticles, searchArticles, getArticle, getCategory, getArticlesByCategory } from '../data/helpArticles';
import { devLog } from '../utils/devLogger';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { IdleLoopVideo } from '../components/chat/IdleLoopVideo';
import { TEEN_VOICES } from './avaLibrary/data';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Help = () => {
  const { categoryId, articleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = (user?.name || '').split(' ')[0] || 'there';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [helpfulFeedback, setHelpfulFeedback] = useState({});
  const [greeter, setGreeter] = useState({ name: 'Maya', avatar_url: '/clare-ava.png', idle_loop_url: null, voice: 'tf1' });
  const [speaking, setSpeaking] = useState(false);
  const audioRef = React.useRef(null);
  const introAudioRef = React.useRef(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [helpSendState, setHelpSendState] = useState('idle'); // idle | sending | sent
  const location = useLocation();
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [myThreads, setMyThreads] = useState(null); // null = loading

  const openReplies = React.useCallback(() => {
    setRepliesOpen(true);
    setMyThreads(null);
    apiClient.request('/api/help/mine')
      .then(d => setMyThreads(d?.requests || []))
      .catch(() => setMyThreads([]));
  }, []);

  // Deep link from the "Maya replied" notification: /help?maya=replies
  useEffect(() => {
    if (new URLSearchParams(location.search).get('maya') === 'replies') openReplies();
  }, [location.search, openReplies]);

  const introText = `Hi ${firstName}! I'm Maya, your guide. Welcome to Help and Support. Search below, or browse the twelve sections — I've organised everything for you. And if you still need help, tap my Maya Helps button at the bottom, write me your message, and my reply will land in your Notifications tab at the top of your dashboard.`;

  // Maya's animated portrait + voice slot (same public greeter as registration)
  React.useEffect(() => {
    apiClient.request('/api/ava-video/registration-greeter')
      .then(d => { if (d && d.name) setGreeter(g => ({ ...g, ...d })); })
      .catch(() => {});
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  const speakIntro = async () => {
    if (speaking) { if (audioRef.current) audioRef.current.pause(); setSpeaking(false); return; }
    try {
      setSpeaking(true);
      if (!introAudioRef.current) {
        const slot = TEEN_VOICES.find(v => v.id === greeter.voice);
        const ttsVoice = slot?.baseVoice || 'nova';
        const d = await apiClient.request('/api/tts/generate', {
          method: 'POST', body: JSON.stringify({ text: introText, voice: ttsVoice }),
        });
        if (!d?.success || !d.audio) { setSpeaking(false); return; }
        const audio = new Audio(`data:audio/mp3;base64,${d.audio}`);
        const pitch = slot?.pitch || 1.0;
        if (pitch !== 1.0) { audio.preservesPitch = false; audio.playbackRate = pitch; }
        introAudioRef.current = audio;
      }
      const audio = introAudioRef.current;
      audio.currentTime = 0;
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      await audio.play();
    } catch { setSpeaking(false); }
  };

  const submitHelpRequest = async () => {
    if (!helpMessage.trim() || helpSendState === 'sending') return;
    setHelpSendState('sending');
    try {
      await apiClient.request('/api/help/request', {
        method: 'POST', body: JSON.stringify({ message: helpMessage.trim() }),
      });
      setHelpSendState('sent');
    } catch {
      setHelpSendState('idle');
    }
  };

  // Analytics: Track article view (moved to top level to follow Rules of Hooks)
  useEffect(() => {
    if (articleId) {
      const article = getArticle(articleId);
      if (article) {
        devLog(`📊 Help Analytics: Article viewed "${article.title}"`);
      }
    }
  }, [articleId]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      const results = searchArticles(query);
      setSearchResults(results);
      // Analytics: Track search query
      devLog(`📊 Help Analytics: Search query "${query}"`);
    } else {
      setSearchResults([]);
    }
  };

  const handleFeedback = (articleId, isHelpful) => {
    setHelpfulFeedback({ ...helpfulFeedback, [articleId]: isHelpful });
    // Analytics: Track helpful feedback
    const feedbackText = isHelpful ? 'YES' : 'NO';
    const article = getArticle(articleId);
    devLog(`📊 Help Analytics: Helpful feedback: ${feedbackText} on article "${article?.title}"`);
    // In production, send to analytics
  };

  const getCategoryColor = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      red: 'bg-red-100 text-red-700 border-red-300',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      pink: 'bg-pink-100 text-pink-700 border-pink-300',
      gray: 'bg-gray-100 text-gray-700 border-gray-300',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      orange: 'bg-orange-100 text-orange-700 border-orange-300',
      rose: 'bg-rose-100 text-rose-700 border-rose-300',
      teal: 'bg-teal-100 text-teal-700 border-teal-300',
      sky: 'bg-sky-100 text-sky-700 border-sky-300'
    };
    return colors[color] || colors.blue;
  };

  // View: Article Detail
  if (articleId) {
    const article = getArticle(articleId);
    const category = getCategory(article?.category);

    if (!article) {
      return (
        <DashboardLayout>
          <div className="p-8 text-center">
            <p className="text-gray-600">Article not found</p>
            <button
              onClick={() => navigate('/help')}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Help Center
            </button>
          </div>
        </DashboardLayout>
      );
    }

    const relatedArticles = article.relatedArticles?.map(id => getArticle(id)).filter(Boolean) || [];

    return (
      <DashboardLayout>
        <div className="p-8 max-w-4xl mx-auto">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <button onClick={() => navigate('/help')} className="hover:text-blue-600">
              <Home size={16} />
            </button>
            <ChevronRight size={16} />
            <button
              onClick={() => navigate(`/help/${category?.id}`)}
              className="hover:text-blue-600"
            >
              {category?.name}
            </button>
            <ChevronRight size={16} />
            <span className="text-gray-900">{article.title}</span>
          </div>

          {/* Article Content */}
          <article className="bg-white rounded-xl shadow-lg p-8 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">{article.title}</h1>

            <div
              className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
              style={{ fontSize: '16px', lineHeight: '1.8' }}
            >
              {article.content.split('\n\n').map((paragraph, i) => {
                // Handle bold text
                const formattedParagraph = paragraph.split('**').map((part, j) =>
                  j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                );

                // Check if it's a list item
                if (paragraph.startsWith('- ')) {
                  return (
                    <ul key={i} className="list-disc ml-6 mb-4">
                      <li>{formattedParagraph}</li>
                    </ul>
                  );
                }

                return <p key={i} className="mb-4">{formattedParagraph}</p>;
              })}
            </div>

            {/* Feedback */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm font-semibold text-gray-900 mb-3">Was this helpful?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleFeedback(article.id, true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                    helpfulFeedback[article.id] === true
                      ? 'bg-green-100 border-green-500 text-green-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ThumbsUp size={18} />
                  <span className="text-sm font-medium">Yes, this helped</span>
                </button>
                <button
                  onClick={() => handleFeedback(article.id, false)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                    helpfulFeedback[article.id] === false
                      ? 'bg-red-100 border-red-500 text-red-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ThumbsDown size={18} />
                  <span className="text-sm font-medium">No, I need more help</span>
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-blue-900 mb-4">Related Articles</h3>
              <div className="space-y-2">
                {relatedArticles.map(related => (
                  <button
                    key={related.id}
                    onClick={() => navigate(`/help/article/${related.id}`)}
                    className="w-full text-left px-4 py-3 bg-white rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-between"
                  >
                    <span className="text-sm font-medium text-gray-900">{related.title}</span>
                    <ChevronRight size={16} className="text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Contact Support */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-8 text-center">
            <Mail size={40} className="mx-auto text-purple-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Still need help?</h3>
            <p className="text-gray-700 mb-4">Our support team is here to assist you</p>
            <button
              onClick={() => window.location.href = 'mailto:support@hibff.com'}
              className="px-8 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2 mb-4"
            >
              <Mail size={18} />
              Contact Support
            </button>
            <div className="text-sm text-gray-600 space-y-1">
              <p>📧 Email: <a href="mailto:support@hibff.com" className="text-blue-600 hover:underline">support@hibff.com</a></p>
              <p>⏱️ Response time: Within 24 hours</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // View: Category Articles List
  if (categoryId) {
    const category = getCategory(categoryId);
    const articles = getArticlesByCategory(categoryId);

    if (!category) {
      return (
        <DashboardLayout>
          <div className="p-8 text-center">
            <p className="text-gray-600">Category not found</p>
            <button
              onClick={() => navigate('/help')}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Help Center
            </button>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="p-8 max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <button onClick={() => navigate('/help')} className="hover:text-blue-600">
              <Home size={16} />
            </button>
            <ChevronRight size={16} />
            <span className="text-gray-900">{category.name}</span>
          </div>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{category.name}</h1>
            <p className="text-gray-600">{category.description}</p>
          </div>

          {/* Articles List */}
          <div className="grid gap-4">
            {articles.map(article => (
              <button
                key={article.id}
                onClick={() => navigate(`/help/article/${article.id}`)}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow text-left flex items-center justify-between"
              >
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {article.content.substring(0, 150)}...
                  </p>
                </div>
                <ChevronRight size={20} className="text-gray-400 flex-shrink-0 ml-4" />
              </button>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // View: Main Help Center
  return (
    <DashboardLayout>
      <div className="p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Help & Support</h1>
          <p className="text-lg text-gray-600">
            Learn how to use HiBFF and get the most out of your experience
          </p>
        </div>

        {/* Maya welcome — large animated, speaking guide */}
        {/* Bug Bounty — earn a unique NFT for every real bug found */}
        <BugBountyCard />

        {/* Maya welcome — large animated, speaking guide */}
        <div className="max-w-3xl mx-auto mb-8 flex flex-col sm:flex-row items-center gap-6 bg-orange-50 border border-orange-200 rounded-3xl p-6 text-left" data-testid="maya-help-intro">
          <div className="relative flex-shrink-0 mb-3 sm:mb-0">
            {greeter.idle_loop_url ? (
              <IdleLoopVideo
                src={`${API_URL}/api/files/${greeter.idle_loop_url}`}
                poster={greeter.avatar_url || '/clare-ava.png'}
                className="w-44 h-44 rounded-3xl object-cover shadow-lg"
                testId="maya-help-idle-loop"
              />
            ) : (
              <img src={greeter.avatar_url || '/clare-ava.png'} alt="Maya" className="w-44 h-44 rounded-3xl object-cover shadow-lg ava-breathe-anim" />
            )}
            <button
              onClick={speakIntro}
              data-testid="maya-hear-btn"
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-md flex items-center gap-1.5 whitespace-nowrap"
              style={{ backgroundColor: '#F5831F' }}
            >
              <Volume2 size={13} className={speaking ? 'animate-pulse' : ''} />
              {speaking ? 'Speaking…' : 'Hear Maya'}
            </button>
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 mb-2">Hi {firstName}! I&apos;m Maya, your guide.</p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Welcome to Help &amp; Support. Search below, or browse the twelve sections — I&apos;ve organised everything for you.
              Still stuck? Tap <strong>&quot;Maya Helps&quot;</strong> at the bottom, write me your message, and my reply will land
              in your <strong>Notifications</strong> tab at the top of your dashboard.
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search size={20} className="absolute left-4 top-3.5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="What do you need help with?"
              className="w-full pl-12 pr-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="max-w-2xl mx-auto mt-4 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-3 bg-gray-50 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-600 uppercase">
                  {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
                </p>
              </div>
              {searchResults.slice(0, 5).map((article) => {
                const category = getCategory(article.category);
                return (
                  <button
                    key={article.id}
                    onClick={() => {
                      navigate(`/help/article/${article.id}`);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="w-full p-4 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded mb-2 ${getCategoryColor(category?.color)}`}>
                          {category?.name}
                        </span>
                        <p className="font-semibold text-gray-900 mb-1">{article.title}</p>
                        <p className="text-sm text-gray-600 line-clamp-1">
                          {article.content.substring(0, 100)}...
                        </p>
                      </div>
                      <ChevronRight size={20} className="text-gray-400 flex-shrink-0 ml-4" />
                    </div>
                  </button>
                );
              })}
              {searchResults.length > 5 && (
                <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
                  <button
                    onClick={() => {
                      // In a real app, this would navigate to a full search results page
                      devLog('See all results clicked');
                    }}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    See all {searchResults.length} results →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Category Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {helpCategories.map(category => (
            <button
              key={category.id}
              onClick={() => navigate(`/help/${category.id}`)}
              className={`p-6 rounded-xl border-2 shadow-md hover:shadow-lg transition-all text-left ${getCategoryColor(category.color)}`}
            >
              <div className="mb-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-3">
                  <span className="text-2xl">{getCategoryIcon(category.icon)}</span>
                </div>
                <h3 className="font-bold text-lg mb-2">{category.name}</h3>
                <p className="text-sm opacity-80">{category.description}</p>
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <span>Learn more</span>
                <ChevronRight size={16} />
              </div>
            </button>
          ))}
        </div>

        {/* Contact Support Banner */}
        <div className="mt-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-8 text-center text-white">
          <Mail size={48} className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Can&apos;t find what you&apos;re looking for?</h2>
          <p className="mb-6 opacity-90">
            Our support team is here to help you 24/7
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => { setHelpModalOpen(true); setHelpSendState('idle'); setHelpMessage(''); }}
              data-testid="maya-helps-btn"
              className="px-8 py-3 bg-orange-400 text-white font-semibold rounded-lg hover:bg-orange-500 transition-colors inline-flex items-center gap-2"
            >
              <Heart size={18} fill="currentColor" />
              Maya Helps
            </button>
            <button
              onClick={openReplies}
              data-testid="maya-my-messages-btn"
              className="px-8 py-3 bg-white/15 border border-white/40 text-white font-semibold rounded-lg hover:bg-white/25 transition-colors inline-flex items-center gap-2"
            >
              <MessageCircle size={18} />
              My Messages to Maya
            </button>
            <button
              onClick={() => navigate('/help/article/contact-support')}
              className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Contact Support
            </button>
          </div>
        </div>

        {/* My Messages to Maya — full threads: teen's message + Maya's reply */}
        {repliesOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="maya-replies-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img src={greeter.avatar_url || '/clare-ava.png'} alt="Maya" className="w-10 h-10 rounded-full object-cover" style={{ border: '2px solid #F5831F' }} />
                  <h3 className="text-lg font-bold text-gray-900">My Messages to Maya</h3>
                </div>
                <button onClick={() => setRepliesOpen(false)} data-testid="maya-replies-close" className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400">
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 space-y-4 pr-1">
                {myThreads === null ? (
                  <div className="py-10 text-center"><Loader2 size={24} className="animate-spin mx-auto text-orange-400" /></div>
                ) : myThreads.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8" data-testid="maya-replies-empty">
                    You haven&apos;t sent Maya a message yet. Tap <strong>Maya Helps</strong> to write one.
                  </p>
                ) : (
                  myThreads.map(t => (
                    <div key={t.request_id} className="border border-gray-100 rounded-xl p-3" data-testid={`maya-thread-${t.request_id}`}>
                      <p className="text-[11px] text-gray-400 mb-1.5">{new Date(t.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      <div className="bg-gray-100 rounded-xl rounded-tr-sm px-3 py-2 ml-8">
                        <p className="text-xs font-semibold text-gray-500 mb-0.5">You</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{t.message}</p>
                      </div>
                      {t.reply ? (
                        <div className="rounded-xl rounded-tl-sm px-3 py-2 mr-8 mt-2" style={{ backgroundColor: '#FFF3EB' }}>
                          <p className="text-xs font-semibold mb-0.5" style={{ color: '#F5831F' }}>Maya</p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap" data-testid={`maya-reply-text-${t.request_id}`}>{t.reply}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic mt-2 mr-8">Maya hasn&apos;t replied yet — the reply will appear here and in your Notifications.</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Maya Helps — teen writes a message, a human replies into Notifications */}
        {helpModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="maya-helps-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img src={greeter.avatar_url || '/clare-ava.png'} alt="Maya" className="w-10 h-10 rounded-full object-cover" style={{ border: '2px solid #F5831F' }} />
                  <h3 className="text-lg font-bold text-gray-900">Maya Helps</h3>
                </div>
                <button onClick={() => setHelpModalOpen(false)} data-testid="maya-helps-close" className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400">
                  <X size={18} />
                </button>
              </div>
              {helpSendState === 'sent' ? (
                <div className="text-center py-6" data-testid="maya-helps-confirmation">
                  <CheckCircle size={44} className="mx-auto mb-3 text-green-500" />
                  <p className="font-semibold text-gray-900 mb-1">Message sent!</p>
                  <p className="text-sm text-gray-600">
                    The HiBFF team reads every message. The reply will land in your <strong>Notifications</strong> tab at the top of your dashboard.
                  </p>
                  <button onClick={() => setHelpModalOpen(false)} className="mt-5 px-6 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: '#F5831F' }}>
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    Tell me what you need help with. A real person reads every message, and the reply lands in your <strong>Notifications</strong> tab at the top of your dashboard.
                  </p>
                  <textarea
                    value={helpMessage}
                    onChange={(e) => setHelpMessage(e.target.value)}
                    rows={4}
                    maxLength={2000}
                    placeholder="Hi Maya, I need help with…"
                    data-testid="maya-helps-textarea"
                    className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                  />
                  <button
                    onClick={submitHelpRequest}
                    disabled={!helpMessage.trim() || helpSendState === 'sending'}
                    data-testid="maya-helps-send"
                    className="mt-3 w-full py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: '#F5831F' }}
                  >
                    {helpSendState === 'sending' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Send to Maya
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Helper function for category icons (using emojis for simplicity)
const getCategoryIcon = (iconName) => {
  const icons = {
    'rocket': '🚀',
    'layout': '📊',
    'users': '👥',
    'shield': '🛡️',
    'credit-card': '💳',
    'heart': '❤️',
    'tool': '🔧',
    'mail': '✉️',
    'graduation': '🎓',
    'target': '🎯',
    'mic': '🎤',
    'family': '👨‍👩‍👧'
  };
  return icons[iconName] || '❓';
};

export default Help;
